/**
 * Reserved `data-*` attribute names used for Vue mounting configuration.
 * These are excluded from component props.
 */
const RESERVED_ATTRIBUTES = new Set(["vue", "appType", "appName"]);

/**
 * Extracts `data-*` attributes from an HTML element and parses them into typed Vue component props.
 *
 * Attributes listed in {@link RESERVED_ATTRIBUTES} are excluded (used for mounting configuration).
 *
 * Type coercion rules:
 * - `"true"` / `"True"` → `true`
 * - `"false"` / `"False"` → `false`
 * - Strings starting with `{` or `[` → parsed as JSON (falls back to string on parse error)
 * - Everything else (including numeric strings) → `string`
 *
 * @param root - The HTML element to extract data attributes from
 * @returns Parsed props object
 */
export function parseDataProps(root: HTMLElement): Record<string, unknown> {
    const dataProps: Record<string, unknown> = {};

    Object.keys(root.dataset)
        .filter((key) => !RESERVED_ATTRIBUTES.has(key))
        .forEach((key) => {
            const value = root.dataset[key];

            if (value === undefined) {
                return;
            }

            dataProps[key] = parseValue(value);
        });

    return dataProps;
}

function parseValue(value: string): string | boolean | Record<string, unknown> | unknown[] {
    if (value === "false" || value === "False") {
        return false;
    }

    if (value === "true" || value === "True") {
        return true;
    }

    if (value.startsWith("{") || value.startsWith("[")) {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }

    return value;
}
